import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { Button } from "@/components/Button";
import { TextField } from "@/components/Field";
import { PASSWORD_PROBLEM_KEY, passwordProblems } from "@/lib/passwordPolicy";

/**
 * The forced password change.
 *
 * Every route redirects here while `mustChangePassword` is set. The password
 * they arrived with was printed on paper and handed to them; it has been seen
 * by whoever printed it and whoever carried it down a corridor.
 *
 * The change happens in two steps and the order matters: Firebase first, then
 * the flag. If the flag were cleared first and the password update failed, the
 * user would keep the printed password with nothing left to prompt them.
 */
export function ChangePasswordPage() {
  const t = useT();
  const navigate = useNavigate();
  const { user, changePassword } = useAuth();
  const { showError, success } = useToast();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  if (user && !user.mustChangePassword) return <Navigate to="/" replace />;

  /*
    The same rule the identity platform enforces at the door.

    Checked here so the answer is immediate rather than a round trip — and
    checked against a shared definition so this screen cannot accept something
    Firebase will refuse, which would leave a teacher guessing which rule they
    broke.
  */
  const problems = password.length > 0 ? passwordProblems(password) : [];
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = problems.length === 0 && password.length > 0 && confirm === password && !busy;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;

    setBusy(true);
    try {
      await changePassword(password);
      success("common.saved");
      navigate("/", { replace: true });
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="flex min-h-screen items-start justify-center bg-canvas px-4 pt-16">
      <div className="card w-full max-w-sm p-8">
        <h1 className="page-title">{t("auth.changePassword")}</h1>
        <p className="mt-2 text-sm text-ink-muted">{t("auth.changePasswordWhy")}</p>

        <form className="mt-8 flex flex-col gap-4" onSubmit={onSubmit}>
          <TextField
            labelKey="auth.newPassword"
            // Said before anybody types, not after they get it wrong.
            hintKey="password.rules"
            type="password"
            autoComplete="new-password"
            autoFocus
            required
            value={password}
            errorKey={problems[0] ? PASSWORD_PROBLEM_KEY[problems[0]] : undefined}
            onChange={(event) => setPassword(event.target.value)}
          />
          <TextField
            labelKey="auth.confirmPassword"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            errorKey={mismatch ? "auth.passwordMismatch" : undefined}
            onChange={(event) => setConfirm(event.target.value)}
          />
          <Button type="submit" busy={busy} disabled={!canSubmit} className="mt-2 w-full">
            {t("auth.save")}
          </Button>
        </form>
      </div>
    </main>
  );
}
